'use client';

import { motion, useReducedMotion } from 'motion/react';
import type { ReactNode } from 'react';

// hero 인트로 진입 애니메이션 (Figma HERO 시퀀스를 delay로 근사).
// hero는 페이지 최상단이라 항상 뷰포트 안 → 마운트 시 1회 재생한다.
// prefers-reduced-motion: transform/fade를 건너뛰고 최종 상태로 렌더 (WCAG 2.3.3).
// Section.Hero는 MotionConfigProvider 바깥이라 여기서 직접 reduced-motion을 처리한다.

// 타이핑이 끝난 뒤 제자리로 올라올 때의 미세 오버슈트 이징 (Figma headline-wrapper y 이징)
const RISE_EASE = [0, 0, 0.282, 1.151] as const;

// 페이드인 + 아래→위 슬라이드 (headline-wrapper·서브타이틀)
// riseDelay를 주면 페이드(opacity)와 상승(y) 타이밍을 분리한다:
//   opacity는 즉시(delay) 나타나 타이핑이 보이고, y는 riseDelay 뒤에 올라와 "제자리를 찾는" 연출.
export function HeroFadeUp({
  children,
  delay = 0,
  y = 24,
  duration = 0.6,
  riseDelay,
  className,
}: {
  children: ReactNode;
  delay?: number;
  y?: number;
  duration?: number;
  riseDelay?: number;
  className?: string;
}) {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) return <div className={className}>{children}</div>;

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        opacity: { delay, duration, ease: 'easeOut' },
        y:
          riseDelay !== undefined
            ? { delay: riseDelay, duration, ease: RISE_EASE }
            : { delay, duration, ease: 'easeOut' },
      }}
    >
      {children}
    </motion.div>
  );
}

// 스케일 팝인(오버슈트) — CTA 버튼 그룹 (Figma scale 0.6 → 1.05 → 1)
// origin으로 확대 기준점을 지정한다. 예) 'right center' → 오른쪽에서 왼쪽으로 커짐
export function HeroPopIn({
  children,
  delay = 0,
  origin = 'center',
  className,
}: {
  children: ReactNode;
  delay?: number;
  origin?: string;
  className?: string;
}) {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) return <div className={className}>{children}</div>;

  return (
    <motion.div
      className={className}
      style={{ transformOrigin: origin }}
      initial={{ opacity: 0, scale: 0.6 }}
      animate={{ opacity: 1, scale: [0.6, 1.05, 1] }}
      transition={{
        // 프로퍼티별 transition은 상위 delay를 상속하지 않으므로 각각에 delay를 지정한다
        scale: { delay, duration: 0.5, ease: 'easeOut' },
        opacity: { delay, duration: 0.3 },
      }}
    >
      {children}
    </motion.div>
  );
}
